// src/utils/netsuiteCheck.js
import { calculateDdsDraw } from "./associateFee";
import { NETSUITE_ACCOUNT_IDS } from "./contants";
import { torontoToday } from "./utils";

const toNum = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

const isMissing = (v) => v === null || v === undefined || v === "";
const hasId = (v) => !isMissing(v) && String(v).trim() !== "";

/**
 * Validate a single practitioner row before creating a NetSuite Check.
 * @returns {{ ok: boolean, errors: string[] }}
 */
export function validateCheckData({
  row,
  accountId = NETSUITE_ACCOUNT_IDS.RBC_SF_CORPORATION_9411,
  subsidiaryId = NETSUITE_ACCOUNT_IDS.SMILES_FIRST_CORPORATION,
}) {
  const errors = [];
  const who = row?.practitioner_name || `Practitioner #${row?.practitioner_id ?? "?"}`;

  if (!row) {
    errors.push("No practitioner row provided.");
    return { ok: false, errors };
  }
  if (!hasId(row.netsuite_vendor_id)) {
    errors.push(`${who}: please set a valid NetSuite Vendor ID on the practitioner profile.`);
  }
  if (!hasId(accountId)) {
    errors.push("Please select a bank account for the check.");
  }
  if (!hasId(subsidiaryId)) {
    errors.push("Please select a subsidiary for the check.");
  }
  if (!hasId(NETSUITE_ACCOUNT_IDS.ASSOCIATES_FEE_PAYABLE)) {
    errors.push('Please configure a valid NetSuite account ID for "Associates Fee Payable".');
  }

  const draw = toNum(calculateDdsDraw(row));
  if (round2(draw) <= 0) {
    errors.push(`${who}: DDS draw is ${draw.toFixed(2)}; nothing to pay.`);
  }

  return { ok: errors.length === 0, errors };
}

/**
 * Build the NetSuite Check payload for a single practitioner row.
 *
 * Bank account and subsidiary default to SFC's RBC account [9411] under the
 * parent subsidiary (Smiles First Corporation) — the usual case. Both are
 * overridable via `accountId` / `subsidiaryId` so a check can instead be drawn
 * on the clinic's own bank account under the clinic's subsidiary; the preview
 * modal exposes them as selects.
 *
 * Payload shape:
 * {
 *   subsidiary: { id: subsidiaryId },
 *   account:    { id: accountId },
 *   entity:     { id: practitioner.netsuite_vendor_id },
 *   tranDate:   "YYYY-MM-DD",
 *   memo:       "Associate Fee - <Month Year> <CLINIC> Smiles Analytics",
 *   expense: { items: [
 *     { account: { id: ASSOCIATES_FEE_PAYABLE }, amount }
 *   ]}
 * }
 */
export function buildCheckPayload({
  trandate,
  memo,
  row,
  accountId = NETSUITE_ACCOUNT_IDS.RBC_SF_CORPORATION_9411,
  subsidiaryId = NETSUITE_ACCOUNT_IDS.SMILES_FIRST_CORPORATION,
}) {
  const { ok, errors } = validateCheckData({ row, accountId, subsidiaryId });
  if (!ok) {
    const err = new Error(
      `Please fix the following before creating the check:\n- ${errors.join("\n- ")}`,
    );
    err.validationErrors = errors;
    throw err;
  }

  const amount = round2(toNum(calculateDdsDraw(row)));

  return {
    subsidiary: { id: String(subsidiaryId) },
    account: { id: String(accountId) },
    entity: { id: String(row.netsuite_vendor_id) },
    tranDate: trandate || torontoToday(),
    memo,
    toBePrinted: true,
    expense: {
      items: [
        {
          account: { id: String(NETSUITE_ACCOUNT_IDS.ASSOCIATES_FEE_PAYABLE) },
          amount,
        },
      ],
    },
  };
}
